import { CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AlgoMode, Currency } from "@front/m19-api-client";
import { CurrencySymbols, StrategyEx } from "@front/m19-models";
import { AuthService } from "@front/m19-services";
import { ModalService, IIconComponent, ITooltipDirective } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  SwitchTargetAlgoModalComponent,
  TargetAlgoModeSelectionData,
} from "@m19-board/insights/advertising-stats/switch-target-algo-modal/switch-target-algo-modal.component";
import { StrategyInfoBlocComponent } from "@m19-board/shared/strategy-info-bloc/strategy-info-bloc.component";

@Component({
  selector: "app-strategy-page-algo-target",
  imports: [
    CurrencyPipe,
    DecimalPipe,
    DatePipe,
    StrategyInfoBlocComponent,
    TranslocoDirective,
    IIconComponent,
    ITooltipDirective,
  ],
  template: `<app-strategy-info-bloc
    (strategyInfoClick)="openAlgoTargetModal()"
    testid="info-algo-target"
    [disabled]="isReadOnly()"
    *transloco="let t"
  >
    <div title class="flex items-center gap-2 text-sm text-gray-500 uppercase">
      <span class="w-[14rem]">{{ t(algoValue().title) }}</span>
      @if (strategy().algoMode === AlgoModes.PRODUCT_LAUNCH || strategy().algoMode === AlgoModes.ACOS_TARGET) {
        <span class="w-[15px]">/</span>
        {{
          strategy().algoMode === AlgoModes.PRODUCT_LAUNCH
            ? t("metrics.DAILY_BUDGET_title")
            : t("sp-strategy-group-page.average_daily_budget")
        }}
      }
    </div>
    <div
      body
      class="flex items-center gap-2 font-medium"
      [attr.data-testid]="
        strategy().algoMode === AlgoModes.PRODUCT_LAUNCH
          ? 'suggested-bid-val'
          : strategy().algoMode === AlgoModes.MONTHLY_BUDGET_TARGET
            ? 'monthly-budget-val'
            : 'acos-target-val'
      "
    >
      <span class="w-[14rem]">
        @if (strategy().algoMode === AlgoModes.PRODUCT_LAUNCH) {
          {{ algoValue().value! | currency: algoValue().unit : "symbol" : "1.2-2" : locale() }}
        }
        @if (strategy().algoMode === AlgoModes.MONTHLY_BUDGET_TARGET) {
          {{ algoValue().value! | currency: algoValue().unit : "symbol" : "1.0-0" : locale() }}
          @if (strategy().nextMonthlyBudget) {
            <span class="text-xs text-gray-400">
              ({{ t("common.next") }}:
              {{ strategy().nextMonthlyBudget! | currency: currencySymbol() : "symbol" : "1.0-0" : locale() }})
            </span>
          }
        }
        @if (strategy().algoMode === AlgoModes.ACOS_TARGET) {
          {{ strategy().acosTarget * 100 | number: "1.0-1" }} %
        }
        @if (spStrategy() && spStrategy()!.algoMode === AlgoModes.TACOS_TARGET) {
          {{ spStrategy()!.tacosTarget! * 100 | number: "1.0-1" }} %
        }
        @if (strategy().algoMode === AlgoModes.AMAZON_MANAGED_BUDGET) {
          @let flight = strategy().budgetFlight;
          @if (flight) {
            {{ flight.budget! | currency: algoValue().unit : "symbol" : "1.0-0" : locale() }}
            <span class="ml-2 text-xs text-gray-400">
              {{ flight.startDate | date: "shortDate" : undefined : locale() }} -
              {{ flight.endDate | date: "shortDate" : undefined : locale() }}
            </span>
          } @else {
            {{ t("common.ended") }}
          }
        }
      </span>
      @if (
        strategy().algoMode !== AlgoModes.MONTHLY_BUDGET_TARGET &&
        strategy().algoMode !== AlgoModes.AMAZON_MANAGED_BUDGET
      ) {
        <span class="w-4"></span>
        @if (dailyBudgetVisible()) {
          <div data-testid="budget-val">
            {{ strategy().dailyBudget! | currency: currencySymbol() : "symbol" : "1.0-0" : locale() }}
          </div>
        } @else {
          <div>-</div>
        }
      }
      @if (tooltip()) {
        <IIcon name="infoCircle" class="text-gray-400" [iTooltip]="tooltip()"></IIcon>
      }
    </div>
  </app-strategy-info-bloc>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageAlgoTargetComponent {
  private readonly modalService = inject(ModalService);
  private readonly authService = inject(AuthService);

  protected readonly AlgoModes = AlgoMode;

  currencyCode = input.required<Currency>();
  strategy = input.required<StrategyEx>();
  organizationId = input.required<number>();
  isReadOnly = input.required<boolean>();
  tooltip = input<string>();

  locale = toSignal(this.authService.userLocale$, { requireSync: true });

  dailyBudgetVisible = computed(() => this.strategy() && this.strategy()!.dailyBudget! > 0);
  currencySymbol = computed(() => CurrencySymbols[this.currencyCode()]);
  spStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy && strategy.isSpStrategy() ? strategy : undefined;
  });
  algoValue = computed(() => this.getStrategyAlgoValues(this.strategy()));

  openAlgoTargetModal() {
    if (this.strategy()?.algoMode === AlgoMode.TACOS_TARGET) return;

    this.modalService.openModal<TargetAlgoModeSelectionData, void>(SwitchTargetAlgoModalComponent, {
      modalTitle: "Change Algorithm Target",
      data: {
        strategy: this.strategy()!,
        organizationId: this.organizationId()!,
        currencyCode: this.currencyCode()!,
      },
    });
  }

  private getStrategyAlgoValues(strategy: StrategyEx): { title: string; value: number | undefined; unit: string } {
    switch (strategy.algoMode) {
      case AlgoMode.MONTHLY_BUDGET_TARGET:
        return {
          title: "overview-grid.monthly_budget",
          value: strategy.monthlyBudget,
          unit: this.currencySymbol()!,
        };
      case AlgoMode.PRODUCT_LAUNCH:
        return {
          title: "switch-target-algo-modal.suggested_bid",
          value: strategy.suggestedBid,
          unit: this.currencySymbol()!,
        };
      case AlgoMode.ACOS_TARGET:
        return { title: "algo-mode-selection.acos_target", value: this.strategy()?.acosTarget, unit: "%" };
      case AlgoMode.TACOS_TARGET: {
        const spStrategy = strategy.isSpStrategy() ? strategy : undefined;
        return { title: "algo-mode-selection.tacos_target", value: spStrategy?.tacosTarget, unit: "%" };
      }
      case AlgoMode.AMAZON_MANAGED_BUDGET:
        return {
          title: "algo-target-renderer.amazon_managed_budget",
          value: strategy.budgetFlight?.budget,
          unit: this.currencySymbol()!,
        };
      default:
        return { title: "", value: undefined, unit: "" };
    }
  }
}
